// The kitchen wall's Home. Version 1 (Scott, 2026-10-13) chose one screen at
// a time from the household's routines (kioskScreen); Kitchen wall v2 (the
// mockup Scott approved 2026-10-14, stage 1) gives every moment the same
// frame: a lead column and a quiet side column split by a hairline, the side
// column's timeline rows, Fraunces for what matters and Instrument Sans for
// the rest.
//
//   out-the-door   full stage: the time and what's left · the grown-ups'
//                  line · each kid's checklist (done steps fold to one line;
//                  the step's icon is its check)
//   rest           the next landmark, large · the kids' school work · dinner ·
//                  notes for the house · the rest of today and tomorrow beside it
//   home           each kid's after-school steps and the week's homework ·
//                  dinner and what's next
//   dinner         tonight's dinner · the question of the day beside it
//   bedtime        steps × kids · lights out as a ring · tomorrow
//   night          tomorrow's one time, large · who has what · the first
//                  thing, the rest of tomorrow and the talk-abouts beside it
//   weekend        what's on now · sometime today · the rest of the day and
//                  the kids' school work beside it
//
// Calmer screens (Scott, 2026-10-14): nothing stacked at the foot of the
// screen. The lead group sits centred in its column, with at most one row of
// supporting detail under it; no spacer pushes anything to the bottom. The
// places (Groceries · Recipes · Call · Notes) live in More and by voice.
//
// More takes the side column (`aside`): the lead stays, dimmed. Presentation
// only: the Shell owns data, permissions and writes. Lists are limited so
// nothing scrolls at 1920×1080.

import { createElement, useState, type ReactNode } from 'react'
import {
  Check, ChevronRight, ShoppingCart, BookOpen, ChefHat, MessageSquare, Shirt, ShoppingBasket, BedDouble,
  Smile, Utensils, Sparkles, Backpack, Footprints, CircleCheck, Folder, Calculator, ShowerHead, BookOpenText, Puzzle, PencilLine, type LucideIcon,
} from 'lucide-react'
import type { FamilyMember } from '@/types/family'
import type { KidRow } from '@/lib/wall/kidDayModel'
import { stillOnToday, type WallTodayRow, type KidSchoolWork } from '@/lib/wall/wallMomentsModel'
import type { BedtimeGrid } from '@/lib/wall/activity/kioskRoutines'
import { minutesOf, type DayAnchors, type KioskScreenPick } from '@/lib/wall/activity/kioskScreen'
import { Photo, type KioskDinner } from './KioskStages'
import type { KidMath } from '@/hooks/useKidMath'

export interface V1Kid {
  member: FamilyMember
  special: string | null
  tomorrowSpecial: string | null
  /** What goes in the bag today: the needed items (permission slip, library book). */
  bag: string[]
  morning: KidRow[]
  after: KidRow[]
  /** School papers (spec 2026-10-20): read-only here; the kid's page acts on them. */
  papers?: V1Paper[]
  /** School work (Scott, 2026-10-29): today's homework and practice, and
   *  homework due later this week. */
  work?: KidSchoolWork
}

export interface V1Paper { id: string; title: string; due: string | null; late: boolean; heldByName: string | null; done: boolean }

/** A kid's open papers as chips. Never ticked from the board: a tap opens
 *  the kid's page. Dashed = not found yet; a name = that parent has it. */
function PaperChips({ kid, papers, onOpen }: { kid: FamilyMember; papers?: V1Paper[]; onOpen: () => void }) {
  const open = (papers ?? []).filter((p) => !p.done)
  if (open.length === 0) return null
  return (
    <ul className="kw-papers" aria-label={`${kid.name}’s papers`}>
      {open.map((p) => (
        <li key={p.id}>
          <button type="button" onClick={onOpen}
            className={`kw-paper ${p.heldByName ? '' : 'is-unfound'} ${p.late ? 'is-late' : ''}`}
            aria-label={`${p.title}${p.due ? `, due ${p.due}` : ''}${p.heldByName ? `, ${p.heldByName} has it` : ', not found yet'}`}>
            <span className="kw-paper-title">{p.title}</span>
            {p.due && <small>{p.due}</small>}
            {p.heldByName && <small className="kw-paper-who">{p.heldByName}</small>}
          </button>
        </li>
      ))}
    </ul>
  )
}

export interface V1Note { id: string; text: string; by: string | null }

export interface KioskHomeV1Props {
  pick: KioskScreenPick
  now: Date
  anchors: DayAnchors
  members: FamilyMember[]
  kids: V1Kid[]
  /** The rest screen's one sentence ("Pickup at 5:30 · Tacos at 6") and a second line. */
  restLine: string
  restSub: string | null
  notes: V1Note[]
  talks: V1Note[]
  dinner: KioskDinner | null
  have: number[]
  scale: number
  question: string | null
  bedtime: BedtimeGrid
  tomorrow: { label: string; outTheDoor: string | null; specials: { member: FamilyMember; text: string }[] }
  weekend: { rows: WallTodayRow[]; anytime: { title: string; owners: string[] }[]; tomorrow: WallTodayRow[]; tomorrowLabel: string }
  onTick: (member: FamilyMember, row: KidRow) => void
  onOpenKid: (member: FamilyMember) => void
  onToggleHave: (index: number) => void
  onScale: (n: number) => void
  onAddMissing: () => void
  onOpenRecipe: () => void
  onStartCooking: () => void
  onAnotherQuestion?: () => void
  onTapRow: (id: string) => void
  /** Opens a piece of school work by its task id: the whole title, the
   *  digest's notes, and Done (Scott, 2026-11-03: "it should be clickable on
   *  the home screen so you can see the details and check them off"). */
  onOpenWork?: (taskId: string) => void
  /** Ticks a task or routine on the day's lists off (Scott, 2026-10-17). */
  onCompleteRow?: (id: string) => void
  /** The scratchpad: the night's talk-abouts open it. */
  onNotes: () => void
  /** More, in place of the side column (Kitchen wall v2). */
  aside?: ReactNode
  /** The conversation with Symphony, in place of the side column (stage 3).
   *  Full-stage moments keep their stage; the reply band shows it there. */
  talk?: ReactNode
  /** Math of the day: each kid's problem and streak, and opening it. */
  math?: KidMath
  onOpenMath?: (member: FamilyMember) => void
}

const SHARED = '__shared'

/** A kid's Math of the day button, under their after-school steps. */
function MathButton({ p, member }: { p: KioskHomeV1Props; member: FamilyMember }) {
  const entry = p.math?.kids.find((k) => k.member.id === member.id)
  if (!entry || !p.onOpenMath) return null
  const status = entry.daily?.status ?? null
  const state = status === 'solved' ? 'Solved' : status === 'revealed' ? 'Done' : 'Ready'
  return (
    <button type="button" className={`kw-math ${status === 'solved' ? 'is-solved' : ''}`} onClick={() => p.onOpenMath!(member)} aria-label={`${member.name}: Math of the day, ${state.toLowerCase()}${entry.streak ? `, ${entry.streak} days in a row` : ''}`}>
      <Calculator aria-hidden="true" />
      <span className="kw-math-grow">Math of the day</span>
      {entry.streak > 0 && <span className="kw-math-star">★ {entry.streak}</span>}
      <span className="kw-math-state">{state}</span>
      <ChevronRight aria-hidden="true" />
    </button>
  )
}

/** A person: their colour and initial, the same everywhere on the wall. */
export function Face({ member, size = 'md' }: { member: Pick<FamilyMember, 'name' | 'initials' | 'color'> | undefined; size?: 'xs' | 'sm' | 'md' | 'lg' }) {
  if (!member) return null
  return (
    <span className={`kv-face is-${size}`} style={{ background: member.color || undefined }} aria-hidden="true">
      {(member.initials || member.name.slice(0, 1)).slice(0, 1)}
    </span>
  )
}

const clockLabel = (min: number) => {
  const h = Math.floor(min / 60), m = min % 60
  return `${h % 12 === 0 ? 12 : h % 12}:${String(m).padStart(2, '0')}`
}
const nowMin = (d: Date) => d.getHours() * 60 + d.getMinutes()
/** "4:15p" / "6p" → "4:15" / "6:00": the side column's times. */
const tlTime = (t: string) => {
  const m = t.match(/^(\d{1,2})(?::(\d{2}))?\s*([ap])?/i)
  return m ? `${m[1]}:${m[2] ?? '00'}` : t
}
const namesOf = (ids: string[] | undefined, members: FamilyMember[]) =>
  (ids ?? []).map((id) => members.find((m) => m.id === id)?.name).filter((n): n is string => !!n)
const byOf = (r: WallTodayRow, members: FamilyMember[]) => r.sub ?? (namesOf(r.owners, members).join(' & ') || null)

/** Tapping the dinner itself goes straight to the recipe, else straight to
 *  cooking step by step (Scott, 2026-10-29: no stop at the servings card). */
const dinnerTap = (p: KioskHomeV1Props): (() => void) | undefined =>
  p.dinner?.hasRecipe ? p.onOpenRecipe : p.dinner?.key ? p.onStartCooking : undefined

/** The dinner's name as one tap into it, or plain text when there is nothing to open. */
function DinnerTitle({ p, className }: { p: KioskHomeV1Props; className: string }) {
  const d = p.dinner!
  const tap = dinnerTap(p)
  if (!tap) return <h2 className={className}>{d.title}</h2>
  return (
    <h2 className={className}>
      <button type="button" className="kw-dinner-tap" onClick={tap} aria-label={`${d.title}: ${d.hasRecipe ? 'open the recipe' : 'start cooking'}`}>{d.title}</button>
    </h2>
  )
}

/** A step's picture, from the words in it; a plain circle-check otherwise.
 *  Order matters: "Dirty clothes in the hamper" is laundry, not dressing;
 *  "Clear the breakfast dishes" is clearing, not eating; "Bag packed:
 *  homework folder" is the bag. */
const STEP_ICONS: [RegExp, LucideIcon][] = [
  [/\b(hamper|laundry)\b/i, ShoppingBasket],
  [/\b(dish(es)?|clear)\b/i, Sparkles],
  [/\b(dress(ed)?|clothes|outfit|uniform|pajamas|pyjamas|pjs)\b/i, Shirt],
  [/\b(shower|bath)\b/i, ShowerHead],
  [/\b(teeth|tooth|brush)\b/i, Smile],
  [/\b(read|reading|book)\b/i, BookOpenText],
  [/\b(bed|room|tidy)\b/i, BedDouble],
  [/\b(breakfast|eat|lunch|snack)\b/i, Utensils],
  [/\b(bag|backpack)\b/i, Backpack],
  [/\b(shoes?|jacket|coat|boots)\b/i, Footprints],
  [/\b(folder)\b/i, Folder],
  [/\b(math|maths)\b/i, Calculator],
  [/\b(puzzle|gal)\b/i, Puzzle],
  [/\b(homework|worksheet|spelling|study|test)\b/i, PencilLine],
]
export function stepIcon(title: string): LucideIcon {
  return STEP_ICONS.find(([re]) => re.test(title))?.[1] ?? CircleCheck
}

function isForKid(row: KidRow, kidId: string) {
  return !row.by || row.by.length === 0 || row.by.includes(kidId)
}

/** A step's own time: one without its own inherits the routine's start, so
 *  a time equal to the start is no time of its own. */
const ownTime = (row: KidRow, startsAt: number | null) => {
  const t = minutesOf(row.timeOfDay)
  return t != null && t !== startsAt ? t : null
}
/** "Bag packed: homework folder, …" — the row says the step; the bag says what. */
const shortTitle = (title: string) => title.split(':')[0].trim() || title

// ─── The frame's pieces ──────────────────────────────────────────────────

/** Lead · hairline · side column. With `aside`, More takes the side column
 *  and the lead stays, dimmed; a full-stage moment gives up ~45% to it. */
function Stage({ lead, side, sideLabel, aside, talk, full, label }: { lead: ReactNode; side?: ReactNode; sideLabel?: string; aside?: ReactNode; talk?: ReactNode; full?: boolean; label: string }) {
  if (aside) {
    return (
      <div className={`kw-stage is-more ${full ? 'was-full' : ''}`}>
        <section className="kw-col is-lead is-dim" aria-label={label}>{lead}</section>
        <div className="kw-vr" aria-hidden="true" />
        <div className="kw-col">{aside}</div>
      </div>
    )
  }
  if (talk && !full) {
    return (
      <div className="kw-stage">
        <section className="kw-col is-lead" aria-label={label}>{lead}</section>
        <div className="kw-vr" aria-hidden="true" />
        <aside className="kw-col kw-side" aria-label="Symphony">{talk}</aside>
      </div>
    )
  }
  if (full || !side) return <div className="kw-stage is-full"><section className="kw-col is-lead" aria-label={label}>{lead}</section></div>
  return (
    <div className="kw-stage">
      <section className="kw-col is-lead" aria-label={label}>{lead}</section>
      <div className="kw-vr" aria-hidden="true" />
      <aside className="kw-col kw-side is-centred" aria-label={sideLabel}>{side}</aside>
    </div>
  )
}

/** A timeline row in the side column; a button when it opens something. */
function Tl({ t, w, by, onTap }: { t: string; w: string; by?: string | null; onTap?: () => void }) {
  const body = <><span className="kw-tl-t">{t}</span><span className="kw-tl-w"><span>{w}</span>{by && <span className="kw-tl-by">{by}</span>}</span></>
  return onTap
    ? <button type="button" className="kw-tl" onClick={onTap}>{body}</button>
    : <div className="kw-tl">{body}</div>
}

/** A task or routine leads with a check that ticks it off (Scott,
 *  2026-10-17); an event has none, only a blank of the same width so the
 *  times line up. The check fills at once; the row leaves when the wall
 *  refetches (done tasks are not on its lists). */
function TodayRows({ rows, p, max }: { rows: WallTodayRow[]; p: KioskHomeV1Props; max: number }) {
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set())
  const shown = rows.slice(0, max)
  const checkable = (r: WallTodayRow) => !!p.onCompleteRow && (r.kind === 'task' || r.kind === 'routine')
  if (!shown.some(checkable)) return <>{shown.map((r) => <Tl key={r.id} t={tlTime(r.time)} w={r.title} by={byOf(r, p.members)} onTap={() => p.onTapRow(r.id)} />)}</>
  return <>{shown.map((r) => {
    const done = ticked.has(r.id)
    return (
      <div key={r.id} className="kw-tl-row">
        {checkable(r) ? (
          <button type="button" className={`kw-tl-ck ${done ? 'is-done' : ''}`} aria-label={`Mark ${r.title} done`} disabled={done}
            onClick={() => { setTicked((s) => new Set(s).add(r.id)); p.onCompleteRow!(r.id) }}>
            <span aria-hidden="true">{done && <Check />}</span>
          </button>
        ) : <span className="kw-tl-ck" aria-hidden="true" />}
        <Tl t={tlTime(r.time)} w={r.title} by={byOf(r, p.members)} onTap={() => p.onTapRow(r.id)} />
      </div>
    )
  })}</>
}

/** Time left as a ring: full when the window opens, empty when it closes. */
function Arc({ frac, size, stroke, children }: { frac: number; size: number; stroke: number; children?: ReactNode }) {
  const r = 50 - stroke / 2 - 1
  const c = 2 * Math.PI * r
  const f = Math.min(1, Math.max(0, frac))
  return (
    <div className="kw-arc" style={{ width: `calc(${size} * var(--p))`, height: `calc(${size} * var(--p))` }}>
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle cx="50" cy="50" r={r} className="kw-arc-track" strokeWidth={stroke} />
        <circle cx="50" cy="50" r={r} className="kw-arc-fill" strokeWidth={stroke} strokeDasharray={c} strokeDashoffset={c * (1 - f)} transform="rotate(-90 50 50)" />
      </svg>
      {children && <div className="kw-arc-label">{children}</div>}
    </div>
  )
}

/** A checklist row: the step's icon is its check (Scott, 2026-10-14). Done,
 *  the circle fills sage and shows a check; the whole row is the toggle. */
function StepRow({ row, next, startsAt, extra, onTick }: { row: KidRow; next: boolean; startsAt?: number | null; extra?: string; onTick: () => void }) {
  const own = startsAt === undefined ? null : ownTime(row, startsAt)
  const short = shortTitle(row.title)
  return (
    <button type="button" onClick={onTick} aria-label={`Mark ${short} ${row.done ? 'not done' : 'done'}`}
      className={`kw-ck ${row.done ? 'is-done' : next ? 'is-next' : ''}`}>
      <span className="kw-ck-c" aria-hidden="true">{createElement(row.done ? Check : stepIcon(row.title))}</span>
      <span className="kw-ck-t"><span>{short}</span>{extra && <small>{extra}</small>}</span>
      {next ? <span className="kw-ck-tag">NEXT</span> : own != null ? <span className="kw-ck-time">{clockLabel(own)}</span> : <span />}
    </button>
  )
}

/** Done steps fold into one line; a tap opens them so one can be unticked. */
function DoneLine({ rows, open, onToggle }: { rows: KidRow[]; open: boolean; onToggle: () => void }) {
  if (!rows.length) return null
  return (
    <button type="button" className="kw-ck is-done is-fold" onClick={onToggle} aria-expanded={open}
      aria-label={open ? 'Hide the done steps' : `Done: ${rows.map((r) => shortTitle(r.title)).join(', ')}. Show them`}>
      <span className="kw-ck-c" aria-hidden="true"><Check /></span>
      <span className="kw-ck-t"><span>{open ? 'Done · tap a step to undo it' : rows.map((r) => shortTitle(r.title)).join(' · ')}</span></span>
      <span />
    </button>
  )
}

/** One kid's checklist: done steps folded, the rest in order; at most `max`
 *  rows counting the folded line, so nothing scrolls (the count in the
 *  kid's heading says how many are left). */
function KidChecklist({ kid, steps, startsAt, bag, max, onTick }: { kid: FamilyMember; steps: KidRow[]; startsAt?: number | null; bag?: string[]; max: number; onTick: (r: KidRow) => void }) {
  const [open, setOpen] = useState(false)
  const done = steps.filter((r) => r.done)
  const todo = steps.filter((r) => !r.done)
  const next = todo[0]
  const bagRow = bag?.length ? steps.find((r) => stepIcon(r.title) === Backpack) : undefined
  const room = Math.max(1, max - (done.length ? 1 : 0))
  const shown = open ? [...done, ...todo].slice(0, room) : todo.slice(0, room)
  return (
    <div className="kw-ck-list" role="group" aria-label={`${kid.name}'s steps`}>
      <DoneLine rows={done} open={open} onToggle={() => setOpen((o) => !o)} />
      {shown.map((r) => (
        <StepRow key={r.id} row={r} next={r === next} startsAt={startsAt}
          extra={r === bagRow ? bag!.join(' · ') : undefined} onTick={() => onTick(r)} />
      ))}
      {steps.length === 0 && <p className="kw-quiet">Nothing on {kid.name}'s list.</p>}
    </div>
  )
}

function KidHead({ kid, meta, count, onOpen }: { kid: FamilyMember; meta?: string | null; count: string; onOpen: () => void }) {
  return (
    <button type="button" className="kw-kid-head" onClick={onOpen}>
      <Face member={kid} size="lg" />
      <span className="kw-grow"><b>{kid.name}</b>{meta && <small>{meta}</small>}</span>
      <span className="kw-meta">{count}</span>
    </button>
  )
}

// ─── School work ─────────────────────────────────────────────────────────

const workKey = (title: string) => title.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

/** The kids' school work, from p.kids[].work (Scott, 2026-10-29: "the
 *  homework/GAL work/etc. due to be worked on this day or week"). One row
 *  per piece of work across the kids, so the sheet both kids have is one
 *  row; a face per kid is that kid's check (sage with a tick once done).
 *  Rows everyone has finished sink to the end. Homework due later this week
 *  follows, one quiet line each, read-only. */
function SchoolWork({ p, max, weekMax }: { p: KioskHomeV1Props; max: number; weekMax: number }) {
  const kids = p.kids.filter((k) => k.work && (k.work.today.length > 0 || k.work.week.length > 0)).slice(0, 3)
  if (!kids.length) return null
  const rows: { key: string; title: string; note: string | null; cells: (KidRow | null)[] }[] = []
  kids.forEach((k, ki) => k.work!.today.forEach(({ row, note }) => {
    const key = `${workKey(row.title)}|${note ?? ''}`
    let r = rows.find((x) => x.key === key && !x.cells[ki])
    if (!r) { r = { key, title: row.title, note, cells: kids.map(() => null) }; rows.push(r) }
    r.cells[ki] = row
  }))
  const allDone = (r: (typeof rows)[number]) => r.cells.every((c) => !c || c.done)
  const shown = [...rows.filter((r) => !allDone(r)), ...rows.filter(allDone)].slice(0, max)
  const week: { id: string; title: string; due: string; who: FamilyMember[] }[] = []
  for (const k of kids) {
    for (const w of k.work!.week) {
      const at = week.find((x) => x.id === w.id || (workKey(x.title) === workKey(w.title) && x.due === w.due))
      if (at) at.who.push(k.member)
      else week.push({ ...w, who: [k.member] })
    }
  }
  const ahead = week.slice(0, weekMax)
  if (!shown.length && !ahead.length) return null
  return (
    <section className="kw-work" aria-label="School work">
      {shown.length > 0 && (
        <>
          <p className="kw-eyebrow">School work today</p>
          <ul className="kw-work-list">
            {shown.map((r) => (
              <li key={r.key} className={`kw-work-row ${allDone(r) ? 'is-done' : ''}`}>
                <span className="kw-work-i" aria-hidden="true">{createElement(stepIcon(r.title))}</span>
                {(() => {
                  const body = <><span>{r.title}</span>{r.note && <small className={r.note === 'late' ? 'is-late' : ''}>{r.note}</small>}</>
                  // A task (homework, a paper) opens; a routine step has nothing more to show.
                  const task = r.cells.find((c) => c?.entityType === 'task')
                  return task && p.onOpenWork
                    ? <button type="button" className="kw-work-t is-open" onClick={() => p.onOpenWork!(task.id)}>{body}</button>
                    : <span className="kw-work-t">{body}</span>
                })()}
                <span className="kw-work-who">
                  {r.cells.map((c, i) => {
                    const k = kids[i]
                    if (!c) return <span key={k.member.id} className="kw-work-na" aria-hidden="true" />
                    return (
                      <button key={k.member.id} type="button" className={`kw-work-ck ${c.done ? 'is-done' : ''}`}
                        aria-pressed={c.done} aria-label={`${k.member.name}: ${r.title}`} onClick={() => p.onTick(k.member, c)}>
                        {c.done ? <span className="kw-work-tick"><Check aria-hidden="true" /></span> : <Face member={k.member} size="md" />}
                      </button>
                    )
                  })}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {ahead.length > 0 && (
        <>
          <p className={`kw-eyebrow ${shown.length ? 'kw-work-lab' : ''}`}>Due this week</p>
          <ul className="kw-work-week">
            {ahead.map((w) => (
              <li key={w.id}>
                <span className="kw-work-due">{w.due}</span>
                {p.onOpenWork
                  ? <button type="button" className="kw-work-wt is-open" onClick={() => p.onOpenWork!(w.id)}>{w.title}</button>
                  : <span className="kw-work-wt">{w.title}</span>}
                <span className="kw-work-faces">{w.who.map((m) => <Face key={m.id} member={m} size="xs" />)}</span>
                <span className="sr-only">{w.who.map((m) => m.name).join(' & ')}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  )
}

/** Whether any kid has school work to show: the quiet screen then makes
 *  room (a smaller landmark; its notes ride the top band instead). */
export const hasSchoolWork = (kids: V1Kid[]) => kids.some((k) => !!k.work && (k.work.today.length > 0 || k.work.week.length > 0))

/** The ids the school work already shows, as the day list writes them
 *  (`task-…`, `routine-…`), so the side column says each thing once. */
const workIds = (p: KioskHomeV1Props) => new Set(p.kids.flatMap((k) => (k.work?.today ?? []).map(({ row }) => `${row.entityType}-${row.id}`)))

/** A kid's homework due later this week, one line under their after-school list. */
function KidWeek({ kid, onOpen }: { kid: V1Kid; onOpen?: (taskId: string) => void }) {
  const week = kid.work?.week ?? []
  if (!week.length) return null
  return (
    <p className="kw-kid-week"><b>This week</b> {week.slice(0, 2).map((w, i) => (
      <span key={w.id}>{i > 0 && ' · '}{onOpen
        ? <button type="button" className="kw-kid-week-open" onClick={() => onOpen(w.id)}>{`${w.title} · ${w.due}`}</button>
        : `${w.title} · ${w.due}`}</span>
    ))}</p>
  )
}

// ─── The moments ────────────────────────────────────────────────────────

/** A step across the kids: one row per step (matched by its words), one
 *  cell per kid; null where that kid's routine doesn't have it. */
interface StepRowModel { key: string; title: string; cells: (KidRow | null)[] }
type OtdKid = V1Kid & { steps: KidRow[]; done: number }

const stepKey = (title: string) => shortTitle(title).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()

/** The union of the kids' steps in routine order: each kid's order is kept,
 *  and a step only one kid has sits after the step before it. */
function stepRows(kids: { steps: KidRow[] }[]): StepRowModel[] {
  const rows: StepRowModel[] = []
  kids.forEach((k, ki) => {
    let at = -1
    for (const r of k.steps) {
      const key = stepKey(r.title)
      let i = rows.findIndex((x) => x.key === key && !x.cells[ki])
      if (i < 0) {
        rows.splice(at + 1, 0, { key, title: r.title, cells: kids.map(() => null) })
        i = at + 1
      }
      rows[i].cells[ki] = r
      at = i
    }
  })
  return rows
}

/** Two halves side by side: split where the first timed step starts
 *  ("Before breakfast" / "Breakfast & out the door") when each half holds
 *  four rows or fewer; else evenly, unlabelled (one table for four or
 *  fewer). Past eight rows, steps
 *  everyone has finished give way first so nothing scrolls. */
const HALF = 4
function splitRows(all: StepRowModel[], startsAt: number | null): { label: string | null; rows: StepRowModel[] }[] {
  let rows = all
  if (rows.length > HALF * 2) {
    const open = rows.filter((r) => r.cells.some((c) => c && !c.done))
    const keep = open.length >= HALF * 2 ? open.slice(0, HALF * 2) : [...rows.filter((r) => !open.includes(r)).slice(-(HALF * 2 - open.length)), ...open]
    rows = all.filter((r) => keep.includes(r))
  }
  if (rows.length <= HALF) return [{ label: null, rows }]
  const timed = (r: StepRowModel) => r.cells.some((c) => c && ownTime(c, startsAt) != null)
  const first = rows.findIndex(timed)
  if (first > 0 && first <= HALF && rows.length - first <= HALF) {
    const lead = rows[first]
    const meal = lead.title.match(/\b(breakfast|lunch|snack)\b/i)?.[1].toLowerCase()
    const own = lead.cells.map((c) => (c ? ownTime(c, startsAt) : null)).find((t) => t != null) ?? 0
    const name = meal ?? clockLabel(own)
    return [
      { label: `Before ${name}`, rows: rows.slice(0, first) },
      { label: `${name[0].toUpperCase()}${name.slice(1)} & out the door`, rows: rows.slice(first) },
    ]
  }
  const mid = Math.ceil(rows.length / 2)
  return [{ label: null, rows: rows.slice(0, mid) }, { label: null, rows: rows.slice(mid) }]
}

/** Half of the morning: the step (its icon, name and time) and one check
 *  circle per kid; the whole cell is the tap target. */
function StepTable({ label, rows, kids, startsAt, next, onTick }: {
  label: string | null; rows: StepRowModel[]; kids: OtdKid[]; startsAt: number | null; next: Map<string, string | null>
  onTick: (member: FamilyMember, row: KidRow) => void
}) {
  return (
    <table className="kw-otd-table" aria-label={label ?? 'Steps'}>
      <thead>
        <tr>
          <th scope="col" className="kw-otd-lab">{label && <span className="kw-eyebrow">{label}</span>}</th>
          {kids.map((k) => (
            <th key={k.member.id} scope="col" className="kw-otd-kidh">
              <Face member={k.member} size="md" />
              <b>{k.member.name}</b>
              {k.special && <small>{k.special} today</small>}
              <small>{k.done} of {k.steps.length}</small>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const isNext = r.cells.some((c, i) => c && next.get(kids[i].member.id) === c.id)
          const time = r.cells.map((c) => (c ? ownTime(c, startsAt) : null)).find((t) => t != null)
          const bags = kids.filter((k, i) => k.bag.length && r.cells[i] && stepIcon(r.title) === Backpack)
          const short = shortTitle(r.title)
          return (
            <tr key={r.key} className={isNext ? 'is-next' : ''}>
              <th scope="row">
                <span className="kw-otd-step">
                  {createElement(stepIcon(r.title), { 'aria-hidden': true })}
                  <span className="kw-otd-name"><span>{short}</span>{bags.length > 0 && <small>{bags.map((k) => (kids.length > 1 ? `${k.member.name}: ${k.bag.join(' · ')}` : k.bag.join(' · '))).join(' · ')}</small>}</span>
                  {time != null && <span className="kw-otd-time">{clockLabel(time)}</span>}
                </span>
              </th>
              {r.cells.map((c, i) => {
                const k = kids[i]
                return (
                  <td key={k.member.id}>
                    {c && (
                      <button type="button" className={`kw-otd-cell ${c.done ? 'is-done' : next.get(k.member.id) === c.id ? 'is-now' : ''}`}
                        aria-pressed={c.done} aria-label={`${k.member.name}: ${short}`} onClick={() => onTick(k.member, c)}>
                        <span className="kw-otd-c" aria-hidden="true">{c.done && <Check />}</span>
                      </button>
                    )}
                  </td>
                )
              })}
            </tr>
          )
        })}
      </tbody>
    </table>
  )
}

/** Getting ready for school (OutTheDoor.dc.html): the time and what's left,
 *  the grown-ups' line, each kid's checklist. */
function OutTheDoor(p: KioskHomeV1Props) {
  const end = p.anchors.morning?.end ?? 0
  const start = p.anchors.morning?.start ?? end - 35
  const startsAt = p.anchors.morning?.start ?? null
  const left = Math.max(0, end - nowMin(p.now))
  const total = end - start + 15
  const kidIds = p.kids.map((k) => k.member.id)
  // The grown-ups' steps: the ones that name only people who aren't kids.
  const seen = new Set<string>()
  const grownUps = p.kids.flatMap((k) => k.morning)
    .filter((r) => r.by?.length && !r.by.some((id) => kidIds.includes(id)))
    .filter((r) => { const key = `${r.title}|${r.timeOfDay}`; if (seen.has(r.id) || seen.has(key)) return false; seen.add(r.id); seen.add(key); return true })
    .sort((a, b) => (minutesOf(a.timeOfDay) ?? 0) - (minutesOf(b.timeOfDay) ?? 0))
    .slice(0, 4)
  // One table, steps × kids (Scott, 2026-10-14): each kid's own steps
  // merged in routine order, in two halves side by side so all fit.
  const kids = p.kids.slice(0, 3).map((k) => {
    const steps = k.morning.filter((r) => isForKid(r, k.member.id) && !(r.by?.length && !r.by.some((id) => kidIds.includes(id))))
    return { ...k, steps, done: steps.filter((r) => r.done).length }
  })
  const rows = stepRows(kids)
  const next = new Map(kids.map((k) => [k.member.id, k.steps.find((r) => !r.done)?.id ?? null]))
  const halves = splitRows(rows, startsAt)
  const lead = (
    <div className="kw-otd">
      <header className="kw-otd-head">
        <h2 className="kw-serif">Out the door at {clockLabel(end)}</h2>
        <span className="kw-grow" />
        <Arc frac={total > 0 ? left / total : 0} size={84} stroke={11} />
        {left > 0
          ? <span className="kw-otd-left" aria-label="Time left"><b className="kw-serif">{left}</b> <span>{left === 1 ? 'minute' : 'minutes'}</span></span>
          : <span className="kw-otd-left" aria-label="Time left"><b className="kw-serif is-word">Time to go</b></span>}
      </header>
      {grownUps.length > 0 && (
        <ul className="kw-grown" aria-label="Grown-ups this morning">
          {grownUps.map((row) => (
            <li key={row.id} className={row.done ? 'is-done' : ''}>
              {(row.by ?? []).slice(0, 2).map((id) => <Face key={id} member={p.members.find((m) => m.id === id)} size="xs" />)}
              <span>{row.timeOfDay ? `${clockLabel(minutesOf(row.timeOfDay) ?? 0)} ` : ''}{row.title}</span>
              <span className="sr-only">{namesOf(row.by, p.members).join(' & ')}</span>
              {row.done && <Check aria-label="done" />}
            </li>
          ))}
        </ul>
      )}
      <div className="kw-otd-tables" style={{ ['--kids' as string]: Math.max(1, kids.length) }}>
        {halves.map((h, i) => (
          <StepTable key={i} label={h.label} rows={h.rows} kids={kids} startsAt={startsAt} next={next} onTick={p.onTick} />
        ))}
      </div>
    </div>
  )
  return <Stage full lead={lead} aside={p.aside} talk={p.talk} label="Getting ready for school" />
}

/** A quiet stretch (Main.dc.html): the next landmark, large; the rest of
 *  today and tomorrow in the side column. */
/** One row of the day's anytime things, with the count of the rest. The
 *  weekend screen keeps the heading even when there are none; the quiet
 *  weekday screen says nothing rather than "Nothing else today". */
function Anytime({ p, always }: { p: KioskHomeV1Props; always?: boolean }) {
  const all = p.weekend.anytime
  if (!all.length && !always) return null
  const shown = all.slice(0, 3)
  return (
    <>
      <div className="kw-anytime-head"><span className="kw-eyebrow">Sometime today</span><span className="kw-meta">{all.length ? `${all.length} to do` : 'Nothing else today'}</span></div>
      {shown.length > 0 && (
        <ul className="kw-anytime" aria-label="Sometime today">
          {shown.map((a) => (
            <li key={a.title}><span className="kw-anytime-dot" aria-hidden="true" /><span>{a.title}{a.owners.length > 0 && <small>{namesOf(a.owners, p.members).join(' & ')}</small>}</span></li>
          ))}
        </ul>
      )}
    </>
  )
}

function Rest(p: KioskHomeV1Props) {
  const [hero, ...rest] = p.restLine.split(' · ')
  const lede = rest.join(' · ') || p.restSub
  const shownAsWork = workIds(p)
  const today = p.weekend.rows.filter((r) => stillOnToday(r) && !shownAsWork.has(r.id))
  const hasWork = hasSchoolWork(p.kids)
  // Five timeline rows fit beside the lead, all of them today's. Tomorrow
  // only steps in once today has nothing left on the clock (Scott,
  // 2026-10-29: the main screen is for today).
  const todayMax = 5
  const tomorrow = today.length ? [] : p.weekend.tomorrow.slice(0, 3)
  const d = p.dinner
  const lead = (
    <>
      <p className="kw-eyebrow">Today</p>
      <h2 className={`kw-hero ${hasWork ? 'is-work' : hero.length > 30 ? 'is-long' : ''}`}>{hero}</h2>
      {lede && <p className="kw-lede is-muted">{lede}</p>}
      <SchoolWork p={p} max={4} weekMax={2} />
      {d && (
        <div className="kw-rest-dinner" role="group" aria-label="Tonight's dinner">
          <p className="kw-eyebrow">{d.timeLabel ?? 'Dinner tonight'}</p>
          <DinnerTitle p={p} className="kw-lede kw-side-title" />
          {d.cue && <p className="kw-meta">{d.cue}</p>}
        </div>
      )}
      {p.notes.length > 0 && !hasWork && (
        <ul className="kw-notes" aria-label="Notes for the house">
          {p.notes.slice(0, 2).map((n, i) => (
            <li key={n.id} className="kw-note" style={{ ['--tilt' as string]: `${[-1.5, 1][i]}deg` }}>
              <p>{n.text}</p>{n.by && <small>{n.by}</small>}
            </li>
          ))}
        </ul>
      )}
      {/* The day's things with no clock — a form to return, a routine that
          runs sometime today (Scott, 2026-10-30: the picture-day form was on
          Today's list and nowhere on the wall). */}
      <Anytime p={p} />
    </>
  )
  const side = (
    <>
      <p className="kw-eyebrow kw-side-lab">The rest of today</p>
      <TodayRows rows={today} p={p} max={todayMax} />
      {today.length === 0 && <p className="kw-quiet">Nothing else on the clock today.</p>}
      {tomorrow.length > 0 && (
        <>
          <p className="kw-eyebrow kw-side-lab is-gap">Tomorrow</p>
          {tomorrow.map((r) => <Tl key={r.id} t={tlTime(r.time)} w={r.title} by={byOf(r, p.members)} />)}
        </>
      )}
    </>
  )
  return <Stage lead={lead} side={side} sideLabel="The rest of today" aside={p.aside} talk={p.talk} label="Today" />
}

/** Dinner in the side column: what, when, and the way in. */
function DinnerSide({ p, after }: { p: KioskHomeV1Props; after: WallTodayRow[] }) {
  const d = p.dinner
  return (
    <>
      <p className="kw-eyebrow">{d?.timeLabel ?? 'Dinner'}</p>
      {d ? (
        <>
          <DinnerTitle p={p} className="kw-lede kw-side-title" />
          {d.cue && <p className="kw-meta">{d.cue}</p>}
          {(d.key || d.hasRecipe) && (
            <div className="kw-acts">
              {d.key && <button type="button" className="kw-btn is-primary" onClick={p.onStartCooking}><ChefHat aria-hidden="true" />Start cooking</button>}
              {d.hasRecipe && <button type="button" className="kw-btn is-quiet" onClick={p.onOpenRecipe}>Recipe</button>}
            </div>
          )}
        </>
      ) : <p className="kw-quiet">Nothing planned yet.</p>}
      <TodayRows rows={after} p={p} max={3} />
    </>
  )
}

/** Home from school (HomeFromSchool.dc.html): each kid's steps · dinner. */
function HomeFromSchool(p: KioskHomeV1Props) {
  const lead = (
    <>
      <p className="kw-eyebrow">After school</p>
      <div className="kw-kids" style={{ ['--kids' as string]: Math.max(1, Math.min(3, p.kids.length)) }}>
        {p.kids.slice(0, 3).map((k) => {
          const rows = k.after.filter((r) => isForKid(r, k.member.id))
          const done = rows.filter((r) => r.done).length
          return (
            <section key={k.member.id} className="kw-kid" aria-label={`${k.member.name} after school`}>
              <KidHead kid={k.member} count={`${done} of ${rows.length}`} onOpen={() => p.onOpenKid(k.member)} />
              <KidChecklist kid={k.member} steps={rows} max={p.math ? 4 : 5} onTick={(r) => p.onTick(k.member, r)} />
              <PaperChips kid={k.member} papers={k.papers} onOpen={() => p.onOpenKid(k.member)} />
              <MathButton p={p} member={k.member} />
              <KidWeek kid={k} onOpen={p.onOpenWork} />
            </section>
          )
        })}
      </div>
    </>
  )
  const after = p.weekend.rows.filter((r) => stillOnToday(r) && !/dinner/i.test(r.title))
  return <Stage lead={lead} side={<DinnerSide p={p} after={after} />} sideLabel="Dinner" aside={p.aside} talk={p.talk} label="After school" />
}

/** Dinner (Dinner.dc.html): tonight's dinner leads; the question of the day
 *  sits in the side column, large. */
function Dinner(p: KioskHomeV1Props) {
  const d = p.dinner
  const t = nowMin(p.now)
  const after = p.weekend.rows.filter((r) => !r.past && r.startsAt != null && nowMin(new Date(r.startsAt)) > t + 30 && !/dinner/i.test(r.title))
  const lead = d ? (
    <>
      {d.imageUrl && (
        <button type="button" className="kw-photo" onClick={d.hasRecipe ? p.onOpenRecipe : undefined} aria-label={d.hasRecipe ? `${d.title}: open the recipe` : d.title}>
          <Photo url={d.imageUrl} />
        </button>
      )}
      <p className="kw-eyebrow">{d.timeLabel ?? 'Tonight'}</p>
      <DinnerTitle p={p} className={`kw-serif kw-dinner-title ${d.imageUrl ? '' : 'is-big'}`} />
      {d.minutes && <p className="kw-sub">{d.minutes} minutes to make{d.ingredients.length ? ` · ${d.ingredients.length} ingredients` : ''}</p>}
      {d.cue && <p className="kw-meta kw-cue">{d.cue}</p>}
      <div className="kw-acts is-under">
        {d.hasRecipe && <button type="button" className="kw-btn is-primary" onClick={p.onOpenRecipe}><BookOpen aria-hidden="true" />Open the recipe</button>}
        {d.key && <button type="button" className={`kw-btn ${d.hasRecipe ? 'is-quiet' : 'is-primary'}`} onClick={p.onStartCooking}><ChefHat aria-hidden="true" />Step by step</button>}
        {d.ingredients.length > 0 && <button type="button" className="kw-btn is-quiet" onClick={p.onAddMissing}><ShoppingCart aria-hidden="true" />What's missing</button>}
      </div>
    </>
  ) : (
    <>
      <p className="kw-eyebrow">Tonight</p>
      <h2 className="kw-serif kw-dinner-title is-big">No dinner planned tonight</h2>
    </>
  )
  const side = (
    <>
      <p className="kw-eyebrow is-sym">Question of the day</p>
      {p.question ? <p className="kw-question">{p.question}</p> : <p className="kw-quiet">No question today.</p>}
      {p.onAnotherQuestion && <button type="button" className="kw-btn is-quiet kw-another" onClick={p.onAnotherQuestion}>Another question</button>}
      {after.length > 0 && <p className="kw-eyebrow kw-side-lab is-gap-lg">After dinner</p>}
      <TodayRows rows={after} p={p} max={2} />
    </>
  )
  return <Stage lead={lead} side={side} sideLabel="Question of the day" aside={p.aside} talk={p.talk} label="Tonight's dinner" />
}

/** Bedtime (Bedtime.dc.html): steps × kids, lights out as a ring, tomorrow. */
function Bedtime(p: KioskHomeV1Props) {
  const g = p.bedtime
  const end = p.anchors.bedtime.end
  const left = Math.max(0, end - nowMin(p.now))
  const span = Math.max(1, end - p.anchors.bedtime.start)
  const people = g.people.slice(0, 3)
  const current = g.steps.find((s) => s.cells.some((c) => c.row && !c.row.done))?.key
  // Six rows fit; past that, steps everyone has finished give way first.
  const MAX = 6
  let steps = g.steps
  if (steps.length > MAX) {
    const open = steps.filter((s) => s.cells.some((c) => c.row && !c.row.done))
    const keep = open.length >= MAX ? open.slice(0, MAX) : [...steps.filter((s) => !open.includes(s)).slice(-(MAX - open.length)), ...open]
    steps = g.steps.filter((s) => keep.includes(s))
  }
  const lead = people.length === 0 ? <p className="kw-quiet">No bedtime routine tonight.</p> : (
    <div className="kw-bed" style={{ ['--cols' as string]: people.length }}>
      <p className="kw-eyebrow kw-bed-lab">{g.title} · {g.done} of {g.total}</p>
      {people.map((who) => (
        <span key={who.id} className="kw-bed-who"><Face member={p.members.find((m) => m.id === who.id) ?? { name: who.name, initials: who.name.slice(0, 1), color: '' } as FamilyMember} size="lg" /><span className="kw-meta">{who.name}</span></span>
      ))}
      {steps.map((s) => (
        <div key={s.key} className="kw-bed-row" role="group" aria-label={s.title}>
          <span className="kw-bed-s">{createElement(stepIcon(s.title), { 'aria-hidden': true })}{s.title}</span>
          {s.cells.filter((c) => people.some((x) => x.id === c.memberId)).map((c) => {
            const name = g.people.find((x) => x.id === c.memberId)?.name ?? ''
            return (
              <span key={c.memberId} className="kw-bed-cell">
                {c.row
                  ? <button type="button" aria-pressed={c.row.done} aria-label={`${name}: ${s.title}`}
                      className={`kw-bed-c ${c.row.done ? 'is-done' : s.key === current ? 'is-now' : ''}`} onClick={() => { const m = p.members.find((x) => x.id === c.memberId); if (m) p.onTick(m, c.row!) }}>
                      {c.row.done && <Check aria-hidden="true" />}
                    </button>
                  : <span className="kw-bed-na" role="img" aria-label={`${name}: not in their routine`} />}
              </span>
            )
          })}
        </div>
      ))}
    </div>
  )
  const specials = p.tomorrow.specials.map((s) => `${s.member.name}: ${s.text}`).join(' · ')
  const side = (
    <>
      <p className="kw-eyebrow">Lights out</p>
      <Arc frac={left / span} size={300} stroke={5}>
        <span className="kw-serif kw-arc-big">{clockLabel(end)}</span>
        <span className="kw-meta">{left > 0 ? `in ${left} minute${left === 1 ? '' : 's'}` : 'now'}</span>
      </Arc>
      <p className="kw-eyebrow kw-side-lab">Tomorrow</p>
      {p.tomorrow.outTheDoor && <Tl t={p.tomorrow.outTheDoor} w="Out the door" by={specials || null} />}
      {p.weekend.tomorrow.slice(0, p.tomorrow.outTheDoor ? 1 : 2).map((r) => <Tl key={r.id} t={tlTime(r.time)} w={r.title} by={byOf(r, p.members)} />)}
      {!p.tomorrow.outTheDoor && p.weekend.tomorrow.length === 0 && <p className="kw-quiet">Nothing on the clock yet.</p>}
    </>
  )
  return <Stage lead={lead} side={side} sideLabel="Lights out" aside={p.aside} talk={p.talk} label="Bedtime" />
}

/** Night (Night.dc.html): tomorrow's one time, large; who has what; the
 *  first thing in the morning and the rest of tomorrow beside it. */
function Night(p: KioskHomeV1Props) {
  const t = p.tomorrow
  const first = t.outTheDoor && p.anchors.morning
    ? [...(p.kids[0]?.morning ?? [])].filter((r) => minutesOf(r.timeOfDay) != null).sort((a, b) => (minutesOf(a.timeOfDay) ?? 0) - (minutesOf(b.timeOfDay) ?? 0))[0] ?? null
    : null
  const lead0 = t.outTheDoor ? null : p.weekend.tomorrow[0] ?? null
  const also = p.weekend.tomorrow.filter((r) => r !== lead0).slice(0, 3)
  const big = t.outTheDoor ?? (lead0 ? tlTime(lead0.time) : null)
  const ampm = t.outTheDoor ? 'am' : lead0 ? (/p/i.test(lead0.time) ? 'pm' : 'am') : ''
  const lead = (
    <>
      <p className="kw-eyebrow">Tomorrow · {t.label}</p>
      {big && <div className="kw-night-time"><b className="kw-serif">{big}</b><small>{ampm}</small></div>}
      <p className={`kw-night-what ${big ? '' : 'is-quiet'}`}>{t.outTheDoor ? 'Out the door' : lead0 ? lead0.title : 'Nothing set for the morning yet.'}</p>
      {t.specials.length > 0 && (
        <div className="kw-specials">
          {t.specials.slice(0, 3).map((s) => (
            <span key={s.member.id} className="kw-who"><Face member={s.member} size="lg" /><span><small>{s.member.name}</small><b>{s.text}</b></span></span>
          ))}
        </div>
      )}
    </>
  )
  const talks = p.talks.length > 0 && (
    <button type="button" className={`kw-btn is-quiet kw-talks ${first || also.length ? 'is-gap' : ''}`} onClick={p.onNotes}>
      <MessageSquare aria-hidden="true" />
      <span><b>{p.talks.length === 1 ? '1 thing' : `${p.talks.length} things`} to talk about</b><small>{p.talks.slice(0, 3).map((x) => x.text).join(' · ')}</small></span>
      <ChevronRight aria-hidden="true" />
    </button>
  )
  const side = (first || also.length > 0 || talks) ? (
    <>
      {first && p.anchors.morning && (
        <>
          <p className="kw-eyebrow">First thing</p>
          <div className="kw-first"><b className="kw-serif">{clockLabel(minutesOf(first.timeOfDay) ?? p.anchors.morning.start)}</b><small>am</small></div>
          <p className="kw-first-what">{first.title}{first.by?.length ? <span className="kw-muted"> · {namesOf(first.by, p.members).join(' & ')}</span> : null}</p>
        </>
      )}
      {also.length > 0 && (
        <>
          <p className={`kw-eyebrow kw-side-lab ${first ? 'is-gap-lg' : ''}`}>Also tomorrow</p>
          {also.map((r) => <Tl key={r.id} t={tlTime(r.time)} w={r.title} by={byOf(r, p.members)} />)}
        </>
      )}
      {talks}
    </>
  ) : null
  return <Stage lead={lead} side={side} sideLabel="Also tomorrow" aside={p.aside} talk={p.talk} label="Tomorrow" />
}

/** A weekend day (Saturday.dc.html): what's on now, sometime today, and the
 *  rest of the day beside it. */
function Weekend(p: KioskHomeV1Props) {
  const rows = p.weekend.rows
  const lead0 = rows.find((r) => r.now && !r.past) ?? rows.find((r) => !r.past) ?? null
  const shownAsWork = workIds(p)
  const later = rows.filter((r) => stillOnToday(r) && r !== lead0 && !shownAsWork.has(r.id))
  const until = lead0?.endsAt ? new Date(lead0.endsAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(/\s?[AP]M$/i, '') : null
  const lead = (
    <>
      {lead0 ? (
        <>
          <p className={`kw-eyebrow ${lead0.now ? 'is-now' : ''}`}>{lead0.now && <span className="kw-dot" aria-hidden="true" />}{lead0.now ? `Now${until ? ` · until ${until}` : ''}` : `Next · ${tlTime(lead0.time)}`}</p>
          <h2 className={`kw-hero is-weekend ${lead0.title.length > 26 ? 'is-long' : ''}`}>{lead0.title}</h2>
          {lead0.sub && <p className="kw-sub">{lead0.sub}</p>}
          {lead0.owners.length > 0 && (
            <div className="kw-people">
              {lead0.owners.slice(0, 5).map((id) => { const m = p.members.find((x) => x.id === id); return m ? <span key={id} className="kw-who"><Face member={m} size="sm" />{m.name}</span> : null })}
            </div>
          )}
        </>
      ) : (
        <>
          <p className="kw-eyebrow">Today</p>
          <h2 className="kw-hero is-weekend">A free day</h2>
          <p className="kw-sub">Nothing on the clock today.</p>
        </>
      )}
      <Anytime p={p} always />
    </>
  )
  // School work takes the side column's foot from tomorrow's preview: the
  // lead has no room for it beside sometime-today (Scott, 2026-10-29).
  const work = hasSchoolWork(p.kids)
  const side = (
    <>
      <p className="kw-eyebrow kw-side-lab">Later today</p>
      <TodayRows rows={later} p={p} max={work || p.weekend.tomorrow.length ? 3 : 5} />
      {later.length === 0 && <p className="kw-quiet">Nothing else on the calendar.</p>}
      {work && <SchoolWork p={p} max={2} weekMax={2} />}
      {!work && p.weekend.tomorrow.length > 0 && (
        <>
          <p className="kw-eyebrow kw-side-lab is-gap">Tomorrow · {p.weekend.tomorrowLabel}</p>
          {p.weekend.tomorrow.slice(0, Math.max(1, 4 - Math.min(3, later.length))).map((r) => <Tl key={r.id} t={tlTime(r.time)} w={r.title} by={byOf(r, p.members)} />)}
        </>
      )}
    </>
  )
  return <Stage lead={lead} side={side} sideLabel="Later today" aside={p.aside} talk={p.talk} label="Today" />
}

export function KioskHomeV1(p: KioskHomeV1Props) {
  switch (p.pick.screen) {
    case 'out-the-door': return <OutTheDoor {...p} />
    case 'home': return <HomeFromSchool {...p} />
    case 'dinner': return <Dinner {...p} />
    case 'bedtime': return <Bedtime {...p} />
    case 'night': return <Night {...p} />
    case 'weekend': return <Weekend {...p} />
    default: return <Rest {...p} />
  }
}

export { SHARED as KV_SHARED }
